import { useCallback } from 'react';
import { useRecoilRefresher_UNSTABLE, useResetRecoilState, useSetRecoilState } from 'recoil';
import { CarModel, StockStatus } from 'models/StockModel';
import carListFamily from 'recoil/atoms/carListFamily';
import spinner from 'recoil/atoms/spinner';
import Api from 'utils/api';
import { removeItemAtIndex, toChunks } from 'utils/common';


const useCars = () => {
    const setCars = useSetRecoilState(carListFamily('pending'));
    const reset = useResetRecoilState(carListFamily('pending'));
    const refresh = useRecoilRefresher_UNSTABLE(carListFamily('pending'));
    const setSpinner = useSetRecoilState(spinner);

    const resetCars = useCallback(() => {
            reset();
            refresh();
        },
        [refresh, reset]);

    const removeFromPending = useCallback((id: number) =>
        setCars(old => removeItemAtIndex(old, old.findIndex(i => i.id === id))),
        // eslint-disable-next-line
    []);

    const cancelCar = useCallback((car: CarModel, reason: string) =>
        Api.cars.cancel(car.id, reason)
            .then(() => removeFromPending(car.id)),
        // eslint-disable-next-line
        []);

    const batchCreate = useCallback((data: {file: File, cols: {[k: string]: number}}) =>
            Api.cars.batchCreate(data)
                .then(res => {
                    resetCars();
                    return res.data;
                }),
        // eslint-disable-next-line
        []);

    const batchUploadEC = useCallback((files: File[]) => {
            const chunked = toChunks(files, 20);

            return Promise.all(chunked.map(files => Api.cars.batchUploadEC(files)))
                .then(resetCars)
        },
        // eslint-disable-next-line
        []);

    const batchUploadECMultiPage = useCallback((data: {companyId: number, data: any, file: File}) => {
            return Api.cars.batchUploadECMultiPage(data)
                .then(resetCars)
        },
        // eslint-disable-next-line
        []);

    const batchUploadPhotos = useCallback((archive: File) =>
            Api.cars.batchUploadPhotos(archive).then(resetCars),
        // eslint-disable-next-line
        []);

    const setStatus = useCallback((id: number, status: StockStatus) =>
            Api.cars.setStatus(id, status)
                .then(() => removeFromPending(id)),
        // eslint-disable-next-line
        []);

    const downloadDocument = (id: number, type: string) => {
        setSpinner(true);
        Api.cars.downloadDocument(id, type)
            .then(() => setSpinner(false))
    };

    return {
        getCar: Api.cars.get,
        getMultiple: Api.cars.getMultiple,
        updateCar: Api.cars.update,
        cancelCar,
        moveToYard: Api.cars.moveToYard,
        setVoyage: Api.cars.book,
        cancelBooking: Api.cars.cancelBooking,
        setShipDateLimit: Api.cars.setShipDateLimit,
        getDocument: Api.cars.getDocument,
        downloadDocument,
        batchCreate,
        batchUploadEC,
        batchUploadECMultiPage,
        batchUploadPhotos,
        putInnerCargo: Api.cars.putInnerCargo,
        changeChassis: Api.cars.changeChassis,
        findCars: Api.cars.find,
        setStatus,
        changeShipper: Api.cars.changeShipper,
        rotatePdf: Api.util.pdfRotate,
    }
};

export default useCars;
